import { AutocompleteChangeReason, Box, Grid, Stack } from '@mui/material';
import { Classification, LocalizedString, Topic } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolTextArea, PoolTextField, PoolTypography } from '@synergy/components';
import { SyntheticEvent, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as TopicActions from './store/topics-actions';
import { ClassificationAutocomplete } from '../../../../common/components/classification-autocomplete/classification-autocomplete';
import { TopicFormikValues, topicFormValuesFieldNames } from './models/topic-formik-values';
import { resetSelectedLanguage } from '../../../../common/actions/selected-language-actions';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { topicsDetailSelector } from './store/topics-selector';
import { topicsDetailValidationSchema } from './topic-detail-validation-schema';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface TopicDetailHandle {
    submitForm: () => Promise<void>;
}

export const TopicDetail = forwardRef((_props, ref) => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const currentTopic = useAppSelector(topicsDetailSelector);
    const formikRef = useRef<FormikProps<TopicFormikValues>>(null);
    const intl = useIntl();
    const id = useParams<{ id: string }>().id;
    const navigate = useNavigate();

    useImperativeHandle<unknown, TopicDetailHandle>(ref, () => ({
        submitForm: async () => {
            const errors = (await formikRef.current?.validateForm()) ?? {};
            if (Object.keys(errors).length > 0) {
                await dispatch(resetSelectedLanguage());
            }
            (await formikRef.current?.submitForm()) ?? Promise.resolve();
        },
    }));

    useEffect(() => {
        if (id && id !== 'new' && currentTopic === null) {
            dispatch(TopicActions.fetchTopicDetail(+id));
        }
    }, [dispatch, id, currentTopic]);

    // Manually set the form values when the topic is fetched
    // EnableReinitialized is triggered also on language change
    // and will reset the form values
    useEffect(() => {
        if (!currentTopic) {
            return;
        }
        formikRef.current?.setValues({
            title: {
                ...currentTopic?.title,
            },
            description: {
                ...currentTopic?.description,
            },
            classifications: currentTopic?.classifications ?? [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTopic]);

    useEffect(() => {
        return () => {
            dispatch(TopicActions.clearTopicDetail());
        };
    }, [dispatch]);

    const handleSubmit = (values: TopicFormikValues) => {
        const topic: Partial<Topic> = {
            id: currentTopic?.id,
            title: values.title,
            description: values.description,
            classifications: values.classifications,
        };

        if (topic?.id && topic.id > 0) {
            dispatch(TopicActions.updateTopic(topic as Topic));
        } else {
            dispatch(TopicActions.createTopic(topic));
            navigate('/configuration/organization/topics');
        }
    };

    const initialValues: TopicFormikValues = {
        title: {
            [selectedLanguage]: '',
            ...currentTopic?.title,
        },
        description: {
            [selectedLanguage]: '',
            ...currentTopic?.description,
        },
        classifications: currentTopic?.classifications ?? [],
    };

    const handleClassificationChange =
        (formikProps: FormikProps<TopicFormikValues>) =>
        (_event: SyntheticEvent, values: Classification[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                formikProps.setFieldValue(topicFormValuesFieldNames.classifications, []);
            } else {
                formikProps.setFieldValue(topicFormValuesFieldNames.classifications, values);
            }
        };

    const getPlaceholder = (value: LocalizedString): string | undefined => {
        return currentLocale !== selectedLanguage
            ? `${currentLocale.toUpperCase()}: ${
                  value[currentLocale] ?? intl.formatMessage({ id: 'app.common.contentMissing' })
              }`
            : undefined;
    };
    return (
        <Box sx={{ mx: theme => theme.general.pageContentSpacing, my: 3 }}>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={topicsDetailValidationSchema(currentLocale)}
                innerRef={formikRef}
            >
                {(formikProps: FormikProps<TopicFormikValues>) => (
                    <Stack gap={6}>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <PoolTypography styleType="sectionTitle">
                                    <FormattedMessage id="app.configuration.topics.info" />
                                </PoolTypography>
                                <Field name={`${topicFormValuesFieldNames.title}.${selectedLanguage}`}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            {...field}
                                            value={field.value ?? ''}
                                            fullWidth={true}
                                            label={<FormattedMessage id="app.configuration.topics.title" />}
                                            placeholder={getPlaceholder(formikProps.values.title)}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && !!meta.error ? (
                                                    <FormattedMessage id={meta.error} />
                                                ) : null
                                            }
                                            required={selectedLanguage === currentLocale}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={`${topicFormValuesFieldNames.description}.${selectedLanguage}`}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            fullWidth={true}
                                            label={<FormattedMessage id="app.configuration.topics.description" />}
                                            placeholder={getPlaceholder(formikProps.values.description)}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && !!meta.error ? (
                                                    <FormattedMessage id={meta.error} />
                                                ) : null
                                            }
                                            required={selectedLanguage === currentLocale}
                                            rows={4}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <PoolTypography styleType="sectionTitle" id="app.configuration.topics.classification" />
                                <Field name={topicFormValuesFieldNames.classifications}>
                                    {({ field, meta }: FieldProps<Classification[]>) => (
                                        <ClassificationAutocomplete
                                            value={field.value}
                                            onChange={handleClassificationChange(formikProps)}
                                            disabled={selectedLanguage !== currentLocale}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Stack>
                )}
            </Formik>
        </Box>
    );
});
